import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'


import { workspacesApi } from './api'
import type { WorkspaceCreate } from './types'

import { keys } from '@/lib/api/keys'

/** The workspace slug is in the URL, and nothing else is authoritative. */
export function useWorkspaceSlug(): string | undefined {
  return useParams<{ workspaceSlug: string }>().workspaceSlug
}

export function useMyWorkspaces() {
  return useQuery({
    queryKey: keys.workspaces.mine(),
    queryFn: workspacesApi.listMine,
    staleTime: 60_000,
  })
}

export function useWorkspace(id: string | undefined) {
  return useQuery({
    queryKey: keys.workspaces.detail(id ?? ''),
    queryFn: () => workspacesApi.get(id!),
    enabled: Boolean(id),
    staleTime: 10 * 60_000,
  })
}

/** What the current user can do here. Refreshed on focus so a role change lands. */
export function useMyAccess(id: string | undefined) {
  return useQuery({
    queryKey: keys.workspaces.access(id ?? ''),
    queryFn: () => workspacesApi.myAccess(id!),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  })
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: WorkspaceCreate) => workspacesApi.create(body),
    // Not optimistic: the server assigns the id and slug, and creates the
    // pipeline, saved views, departments and subscription.
    //
    // Two things this has to get right, and it used to get neither.
    //
    // RETURNED, not fired and forgotten: React Query awaits a promise returned
    // here before running the caller's own onSuccess — which is where
    // onboarding navigates to /w/:slug.
    //
    // refetchType 'all', because nothing is subscribed to this query while the
    // onboarding form is on screen. The default only refetches ACTIVE queries,
    // so the list was marked stale and left alone; the guard on the next route
    // then read the cached empty array, concluded the user had no workspaces,
    // and sent them straight back to onboarding. Creating a workspace — the
    // first thing anyone does here — appeared to do nothing at all.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: keys.workspaces.mine(),
        refetchType: 'all',
      }),
  })
}

/**
 * Switching workspaces clears the ENTIRE query cache.
 *
 * This is a tenancy requirement, not a performance choice: a cached lead from
 * workspace A must not be renderable while workspace B is on screen.
 */
export function useSwitchWorkspace() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return (slug: string, module = '') => {
    queryClient.clear()
    void navigate(`/w/${slug}${module}`)
  }
}
